import type ExcelJS from 'exceljs';
import type { ProductExportRow } from '@/api/models';
import { bpsToFraction, gelNumberOf } from '@/utils';
import { joinVariantOptionNames } from '../productsMeta';
import {
    EXPORT_MONEY_FORMAT,
    EXPORT_PERCENT_FORMAT,
    PRODUCT_EXPORT_COLUMNS,
    type ProductExportColumnKey,
    type ProductExportColumnKind,
    type ProductExportLabels,
} from './productExportMeta';

/**
 * Builds the products export's workbook (bos-app#120) from the rows bos-api
 * sent and the copy the View resolved: a "Products" sheet, one row per
 * variant, and a "Filters" sheet saying what the file holds.
 *
 * exceljs is ~1.4 MB, so it is loaded here through a dynamic `import()`: the
 * build splits it into its own chunk, fetched the first time someone exports
 * (or downloads the purchase template). Pure of React and i18next, so it
 * tests by reading the workbook back.
 */

type CellValue = string | number | null;

/** The number format a column's cells carry; `null` leaves Excel's own. */
const CELL_FORMAT: Record<ProductExportColumnKind, string | null> = {
    text: null,
    number: null,
    money: EXPORT_MONEY_FORMAT,
    percent: EXPORT_PERCENT_FORMAT,
};

const moneyOf = (tetri: number | null): CellValue =>
    tetri === null ? null : gelNumberOf(tetri);

const textOrNull = (text: string): CellValue => (text === '' ? null : text);

/**
 * One row's cell in each column. Money and markup are numbers the column's
 * format shows as ₾ and percent; an unknown one (no stock, so no cost) is an
 * empty cell, never 0.
 */
export const productExportCells = (
    row: ProductExportRow,
    labels: ProductExportLabels,
): Record<ProductExportColumnKey, CellValue> => ({
    productName: row.productName,
    sku: row.sku,
    type: labels.types[row.type],
    category: textOrNull(row.categoryPath.join(labels.categorySeparator)),
    brand: row.brand,
    variant: textOrNull(joinVariantOptionNames(row.optionValues)),
    // Text, so a leading zero survives.
    barcode: row.barcode,
    // One text cell, in the detail's order; empty when there are none.
    alternateBarcodes: textOrNull(row.alternateBarcodes.join(', ')),
    unit: labels.units[row.unit] ?? row.unit,
    quantity: row.quantity,
    expiredQuantity: row.expiredQuantity,
    minimumQuantity: row.minimumQuantity,
    currentCost: moneyOf(row.currentCostTetri),
    salePrice: moneyOf(row.salePriceTetri),
    markup: row.markupBps === null ? null : bpsToFraction(row.markupBps),
    wholesalePrice: moneyOf(row.wholesalePriceTetri),
    lowStock: row.lowStock ? labels.yes : labels.no,
    archived: row.archived ? labels.yes : labels.no,
    draft: row.draft ? labels.yes : labels.no,
});

/**
 * The workbook: the Products sheet first, the one a reader lands on, with a
 * frozen header row that carries column filters; then the Filters sheet,
 * ending with `exportedAt`, when the file was made.
 */
export const buildProductExportWorkbook = async (
    rows: readonly ProductExportRow[],
    labels: ProductExportLabels,
    exportedAt: string,
): Promise<ExcelJS.Workbook> => {
    const { Workbook } = (await import('exceljs')).default;
    const workbook = new Workbook();

    const products = workbook.addWorksheet(labels.sheets.products, {
        views: [{ state: 'frozen', ySplit: 1 }],
    });
    products.columns = PRODUCT_EXPORT_COLUMNS.map((column) => ({
        key: column.key,
        header: labels.columns[column.key] ?? column.key,
        width: column.width,
    }));
    products.getRow(1).font = { bold: true };
    products.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: PRODUCT_EXPORT_COLUMNS.length },
    };
    rows.forEach((row) => {
        const sheetRow = products.addRow(productExportCells(row, labels));
        PRODUCT_EXPORT_COLUMNS.forEach((column) => {
            const format = CELL_FORMAT[column.kind];
            if (format !== null) {
                sheetRow.getCell(column.key).numFmt = format;
            }
        });
    });

    const filters = workbook.addWorksheet(labels.sheets.filters);
    filters.columns = [
        { key: 'label', header: labels.filters.heading.label, width: 28 },
        { key: 'value', header: labels.filters.heading.value, width: 64 },
    ];
    filters.getRow(1).font = { bold: true };
    labels.filters.lines.forEach((line) => {
        filters.addRow({ label: line.label, value: line.value });
    });
    filters.addRow({ label: labels.filters.exportedAt, value: exportedAt });

    return workbook;
};
